<template>
  <HrPageLayout
    title="Redeployment Plan"
    subtitle="Official register of redeployment plans: review the list, export records, and open the editor to draft or amend a plan in line with the authorised approval and sign-off workflow."
    icon=""
    header-tone="institutional"
    :breadcrumb-items="breadcrumbItems"
  >
    <div v-if="!allowed" class="rdp-no-access">
      <div class="rdp-no-access-icon"><i class="bx bx-lock-alt"></i></div>
      <h5>Access Restricted</h5>
      <p>You do not have permission to view this module.</p>
    </div>
    <div v-else class="plan-wrap">

      <!-- Toolbar -->
      <div class="rdp-toolbar-modern">
        <div class="rdp-toolbar-tabs">
          <button class="rdp-tab" :class="{ 'rdp-tab-active': !editorOpen }" @click="closeEditor">
            <i class="bx bx-list-ul"></i>
            <span>Plans List</span>
            <span v-if="total" class="rdp-tab-badge">{{ total }}</span>
          </button>
          <button v-if="editorOpen" class="rdp-tab rdp-tab-active">
            <i class="bx bx-edit-alt"></i>
            <span>{{ form.id ? 'Edit Plan' : 'New Plan' }}</span>
          </button>
        </div>
        <div class="rdp-toolbar-actions">
          <button class="rdp-btn rdp-btn-ghost" :disabled="loadingList" @click="loadPlans">
            <i class="bx bx-refresh" :class="{ 'bx-spin': loadingList }"></i> Refresh
          </button>
          <button class="rdp-btn rdp-btn-outline" :disabled="loadingList || !plans.length" @click="exportPlansCsv">
            <i class="bx bx-download"></i> Export CSV
          </button>
          <button v-if="canManage" class="rdp-btn rdp-btn-primary" @click="openCreatePlan">
            <i class="bx bx-plus"></i> New Plan
          </button>
        </div>
      </div>

      <!-- Plans List -->
      <div v-if="!editorOpen" class="rdp-card">
        <div class="rdp-card-header">
          <div class="rdp-card-title-group">
            <span class="rdp-card-title"><i class="bx bx-spreadsheet"></i> Redeployment Plans</span>
            <span class="rdp-badge-count">{{ total }} plan{{ total !== 1 ? 's' : '' }}</span>
          </div>
        </div>

        <div v-if="loadingList" class="rdp-loading">
          <div class="rdp-spinner"></div>
          <span>Loading plans...</span>
        </div>

        <div v-else-if="!plans.length" class="rdp-empty">
          <div class="rdp-empty-icon"><i class="bx bx-folder-open"></i></div>
          <h5>No plans yet</h5>
          <p class="text-muted">{{ canManage ? 'Click New Plan to get started.' : 'No redeployment plans have been created.' }}</p>
          <button v-if="canManage" class="rdp-btn rdp-btn-primary mt-3" @click="openCreatePlan">
            <i class="bx bx-plus"></i> Create First Plan
          </button>
        </div>

        <div v-else class="rdp-table-wrap">
          <table class="rdp-table">
            <thead>
              <tr>
                <th style="width:44px">#</th>
                <th>Plan ID</th>
                <th>Plan Title</th>
                <th>Sponsor</th>
                <th>Effective Date</th>
                <th>Review Date</th>
                <th>Status</th>
                <th style="width:120px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in plans" :key="row.id" class="rdp-tr">
                <td class="rdp-td-num">{{ (page - 1) * limit + idx + 1 }}</td>
                <td><span class="rdp-plan-id">{{ row.plan_id || '—' }}</span></td>
                <td><span class="rdp-plan-title">{{ row.plan_title || '—' }}</span></td>
                <td class="text-muted">{{ row.sponsor || '—' }}</td>
                <td class="text-muted">{{ row.effective_date || '—' }}</td>
                <td class="text-muted">{{ row.review_date || '—' }}</td>
                <td><span class="rdp-status-pill" :class="statusClass(row.status)">{{ row.status || 'Draft' }}</span></td>
                <td>
                  <div class="rdp-row-actions">
                    <button v-if="canManage" class="rdp-action-btn rdp-action-edit" @click="editPlan(row)" title="Edit Plan">
                      <i class="bx bx-edit-alt"></i>
                    </button>
                    <button class="rdp-action-btn rdp-action-download"
                      :disabled="!canDownloadRow(row) || downloadingPdf"
                      :title="canDownloadRow(row) ? 'Download PDF' : rowDownloadReason(row)"
                      @click="downloadPlanPdf(row)">
                      <i class="bx bx-file-pdf"></i>
                    </button>
                    <button class="rdp-action-btn rdp-action-export" title="Export CSV" @click="exportPlanRowCsv(row)">
                      <i class="bx bx-export"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="total > limit" class="rdp-pagination">
          <span class="rdp-pagination-info">Page {{ page }} of {{ totalPages }} &nbsp;·&nbsp; {{ total }} total</span>
          <nav class="rdp-pages">
            <button class="rdp-page-btn" :disabled="page <= 1" @click="page--; loadPlans()">
              <i class="bx bx-chevron-left"></i>
            </button>
            <button class="rdp-page-btn" :disabled="page >= totalPages" @click="page++; loadPlans()">
              <i class="bx bx-chevron-right"></i>
            </button>
          </nav>
        </div>
      </div>

      <!-- Editor -->
      <div v-if="editorOpen" id="plan-editor" class="rdp-editor-card">
        <div class="rdp-editor-header">
          <div class="rdp-editor-header-left">
            <div class="rdp-editor-icon"><i class="bx bx-file-blank"></i></div>
            <div>
              <h4 class="rdp-editor-title">{{ form.id ? 'Edit Redeployment Plan' : 'Create Redeployment Plan' }}</h4>
              <p class="rdp-editor-subtitle">
                <span v-if="form.plan_id" class="rdp-plan-id me-2">{{ form.plan_id }}</span>
                Complete all 11 sections. PDF download requires all four signatories.
              </p>
            </div>
          </div>
          <div class="rdp-editor-header-actions">
            <button class="rdp-btn rdp-btn-ghost" @click="closeEditor">
              <i class="bx bx-arrow-back"></i> Back to List
            </button>
            <button class="rdp-btn rdp-btn-outline-success"
              :disabled="!canDownloadCurrent || downloadingPdf"
              :title="downloadBlockedReason"
              @click="downloadPlanPdf()">
              <i class="bx bx-file-pdf"></i>
              {{ downloadingPdf ? 'Generating...' : 'Download PDF' }}
            </button>
          </div>
        </div>

        <!-- Progress indicator -->
        <div class="rdp-progress-bar">
          <div class="rdp-progress-fill" :style="{ width: canDownloadCurrent ? '100%' : '75%' }"></div>
        </div>

        <div class="rdp-editor-body">
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-info-circle"></i> 1. Plan Header</div>
          <div class="rdp-section-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Plan Title <span class="text-danger">*</span></label>
            <input v-model="form.plan_title" type="text" class="form-control" placeholder="e.g. Health Services Redeployment Plan FY 2026/27">
          </div>
          <div class="col-md-3">
            <label class="form-label">Effective Date <span class="text-danger">*</span></label>
            <input v-model="form.effective_date" type="date" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Review Date</label>
            <input v-model="form.review_date" type="date" class="form-control">
          </div>

          <div class="col-md-6">
            <label class="form-label">Sponsor <span class="text-danger">*</span></label>
            <input v-model="form.sponsor" type="text" class="form-control" placeholder="e.g. CECM - Public Service">
          </div>
          <div class="col-md-6">
            <label class="form-label">Status</label>
            <select v-model="form.status" class="form-control">
              <option v-for="s in statuses" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>

          <div class="col-12">
            <label class="form-label">Objective <span class="text-danger">*</span></label>
            <textarea v-model="form.objective" rows="3" class="form-control" placeholder="State why this redeployment plan is required."></textarea>
          </div>

          <div class="col-12">
            <label class="form-label">Scope <span class="text-danger">*</span></label>
            <textarea v-model="form.scope" rows="3" class="form-control" placeholder="Define affected departments, roles, stations, and implementation boundaries."></textarea>
          </div>
        </div>
          </div>
        </div>

        <!-- 2 Business Cases -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-briefcase"></i> 2. Business Cases <span class="rdp-section-hint">(repeatable)</span></div>
          <div class="rdp-section-body">
        <p class="small text-muted mb-2">Each row is one independent case (Driver, Nurse, Accountant, etc.).</p>
        <div v-for="(bc, idx) in details.business_cases" :key="'bc-' + idx" class="repeat-block mb-3">
          <div class="row g-2">
            <div class="col-md-4">
              <label class="form-label small">Case Type</label>
              <input v-model="bc.case_type" type="text" class="form-control form-control-sm" placeholder="e.g. Driver, Nurse, Accountant">
            </div>
            <div class="col-md-8">
              <label class="form-label small">Title</label>
              <input v-model="bc.title" type="text" class="form-control form-control-sm" placeholder="Short title for this case">
            </div>
            <div class="col-12">
              <label class="form-label small">Description</label>
              <textarea v-model="bc.description" rows="2" class="form-control form-control-sm" placeholder="What triggered this case (budget, restructuring, project end, etc.)"></textarea>
            </div>
            <div class="col-md-6">
              <label class="form-label small">Expected Benefit</label>
              <textarea v-model="bc.expected_benefit" rows="2" class="form-control form-control-sm" placeholder="Cost saving, efficiency, skill alignment"></textarea>
            </div>
            <div class="col-md-6">
              <label class="form-label small">Risk if Not Implemented</label>
              <textarea v-model="bc.risk_if_not" rows="2" class="form-control form-control-sm" placeholder="Talent loss, delays, extra cost"></textarea>
            </div>
            <div class="col-12 text-end">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="removeBusinessCase(idx)">Remove case</button>
            </div>
          </div>
        </div>
        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="addBusinessCase">+ Add Business Case</button>
          </div>
        </div>

        <!-- 3 Impacted Roles -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-group"></i> 3. Impacted Roles &amp; Employees</div>
          <div class="rdp-section-body">
        <div class="table-responsive border rounded">
          <table class="table table-sm table-bordered mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th>Current Role</th>
                <th>Employee Name</th>
                <th>Department</th>
                <th>Proposed New Role</th>
                <th>New Dept/Location</th>
                <th>Skills Gap</th>
                <th style="min-width: 120px">Consultation Status</th>
                <th style="width: 70px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in details.impacted_roles" :key="'ir-' + idx">
                <td><input v-model="row.current_role" class="form-control form-control-sm" placeholder="e.g. Sales Representative"></td>
                <td><input v-model="row.employee_name" class="form-control form-control-sm" placeholder="Full name"></td>
                <td><input v-model="row.department" class="form-control form-control-sm" placeholder="Current department"></td>
                <td><input v-model="row.proposed_new_role" class="form-control form-control-sm" placeholder="New role"></td>
                <td><input v-model="row.new_dept_location" class="form-control form-control-sm" placeholder="e.g. Nairobi HQ"></td>
                <td><input v-model="row.skills_gap" class="form-control form-control-sm" placeholder="e.g. CRM training"></td>
                <td>
                  <select v-model="row.consultation_status" class="form-select form-select-sm">
                    <option v-for="o in workflowStatuses" :key="o" :value="o">{{ o }}</option>
                  </select>
                </td>
                <td><button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger w-100" @click="removeImpactedRole(idx)">✕</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary mt-2" @click="addImpactedRole">+ Add row</button>
          </div>
        </div>

        <!-- 4 Selection Criteria -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-filter"></i> 4. Selection Criteria</div>
          <div class="rdp-section-body">
        <textarea v-model="details.selection_criteria" rows="3" class="form-control" placeholder="Explain selection basis (skills, performance, tenure, etc.)"></textarea>
          </div>
        </div>

        <!-- 5 Timeline -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-calendar-check"></i> 5. Timeline &amp; Milestones</div>
          <div class="rdp-section-body">
        <div class="table-responsive border rounded">
          <table class="table table-sm table-bordered mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th style="min-width: 130px">Due Date</th>
                <th>Activity</th>
                <th>Owner</th>
                <th style="min-width: 120px">Status</th>
                <th style="width: 70px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in details.timeline" :key="'tl-' + idx">
                <td><input v-model="row.due_date" type="date" class="form-control form-control-sm"></td>
                <td><input v-model="row.activity" class="form-control form-control-sm" placeholder="e.g. Announce plan"></td>
                <td><input v-model="row.owner" class="form-control form-control-sm" placeholder="Responsible person"></td>
                <td>
                  <select v-model="row.status" class="form-select form-select-sm">
                    <option v-for="o in workflowStatuses" :key="'t-' + o" :value="o">{{ o }}</option>
                  </select>
                </td>
                <td><button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger w-100" @click="removeTimeline(idx)">✕</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary mt-2" @click="addTimeline">+ Add milestone</button>
          </div>
        </div>

        <!-- 6 Consultation -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-chat"></i> 6. Consultation &amp; Communication Plan</div>
          <div class="rdp-section-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small">Key Messages</label>
                <textarea v-model="details.consultation.key_messages" rows="2" class="form-control form-control-sm" placeholder="Rationale and next steps"></textarea>
              </div>
              <div class="col-md-6">
                <label class="form-label small">Channels</label>
                <textarea v-model="details.consultation.channels" rows="2" class="form-control form-control-sm" placeholder="Email, meetings, etc."></textarea>
              </div>
              <div class="col-md-6">
                <label class="form-label small">Feedback Mechanism</label>
                <input v-model="details.consultation.feedback_mechanism" class="form-control form-control-sm" placeholder="Survey, email">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Union/Consultation Details</label>
                <input v-model="details.consultation.union_details" class="form-control form-control-sm" placeholder="Dates or N/A">
              </div>
            </div>
          </div>
        </div>

        <!-- 7 Training -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-book-open"></i> 7. Training &amp; Support</div>
          <div class="rdp-section-body">
            <div class="table-responsive border rounded">
              <table class="table table-sm table-bordered mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Employee Name</th>
                    <th>Skills Gap</th>
                    <th>Training Required</th>
                    <th>Duration</th>
                    <th style="width: 90px">Cost</th>
                    <th>Provider</th>
                    <th style="min-width: 120px">Completion Date</th>
                    <th style="width: 70px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in details.training_support" :key="'tr-' + idx">
                    <td><input v-model="row.employee_name" class="form-control form-control-sm" placeholder="Full name"></td>
                    <td><input v-model="row.skills_gap" class="form-control form-control-sm" placeholder="e.g. System training"></td>
                    <td><input v-model="row.training_required" class="form-control form-control-sm" placeholder="Course/program"></td>
                    <td><input v-model="row.duration" class="form-control form-control-sm" placeholder="e.g. 2 weeks"></td>
                    <td><input v-model.number="row.cost" type="number" step="any" class="form-control form-control-sm" placeholder="Amount"></td>
                    <td><input v-model="row.provider" class="form-control form-control-sm" placeholder="Internal/External"></td>
                    <td><input v-model="row.completion_date" type="date" class="form-control form-control-sm"></td>
                    <td><button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger w-100" @click="removeTraining(idx)">✕</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary mt-2" @click="addTraining">+ Add row</button>
          </div>
        </div>

        <!-- 8 Terms -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-file-blank"></i> 8. Terms &amp; Conditions Changes</div>
          <div class="rdp-section-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small">Salary Change</label>
                <input v-model="details.terms_conditions.salary_change" class="form-control form-control-sm" placeholder="Describe or N/A">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Grade Change</label>
                <input v-model="details.terms_conditions.grade_change" class="form-control form-control-sm" placeholder="New grade">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Location Change</label>
                <input v-model="details.terms_conditions.location_change" class="form-control form-control-sm" placeholder="New location">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Working Hours Change</label>
                <input v-model="details.terms_conditions.working_hours_change" class="form-control form-control-sm" placeholder="Updated hours">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Reporting Line Change</label>
                <input v-model="details.terms_conditions.reporting_line_change" class="form-control form-control-sm" placeholder="New supervisor">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Benefits Change</label>
                <input v-model="details.terms_conditions.benefits_change" class="form-control form-control-sm" placeholder="Updated benefits">
              </div>
              <div class="col-md-6">
                <label class="form-label small">Legal/HR Review Status</label>
                <select v-model="details.terms_conditions.legal_hr_review_status" class="form-select form-select-sm">
                  <option v-for="o in legalReviewStatuses" :key="o" :value="o">{{ o }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- 9 Risk -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-shield-alt-2"></i> 9. Risk &amp; Mitigation Log</div>
          <div class="rdp-section-body">
            <div class="table-responsive border rounded">
              <table class="table table-sm table-bordered mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Risk</th>
                    <th style="width: 100px">Likelihood</th>
                    <th style="width: 100px">Impact</th>
                    <th>Mitigation</th>
                    <th>Owner</th>
                    <th style="width: 70px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in details.risk_mitigation" :key="'rk-' + idx">
                    <td><input v-model="row.risk" class="form-control form-control-sm" placeholder="e.g. Staff resignations"></td>
                    <td>
                      <select v-model="row.likelihood" class="form-select form-select-sm">
                        <option v-for="o in riskLevels" :key="'l-' + o" :value="o">{{ o }}</option>
                      </select>
                    </td>
                    <td>
                      <select v-model="row.impact" class="form-select form-select-sm">
                        <option v-for="o in riskLevels" :key="'i-' + o" :value="o">{{ o }}</option>
                      </select>
                    </td>
                    <td><input v-model="row.mitigation" class="form-control form-control-sm" placeholder="Action plan"></td>
                    <td><input v-model="row.owner" class="form-control form-control-sm" placeholder="Responsible person"></td>
                    <td><button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger w-100" @click="removeRisk(idx)">✕</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary mt-2" @click="addRisk">+ Add row</button>
          </div>
        </div>

        <!-- 10 Success metrics -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-bar-chart-alt-2"></i> 10. Success Metrics</div>
          <div class="rdp-section-body">
            <div class="table-responsive border rounded">
              <table class="table table-sm table-bordered mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Metric</th>
                    <th>Target</th>
                    <th>Actual</th>
                    <th>Notes</th>
                    <th style="width: 70px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in details.success_metrics" :key="'sm-' + idx">
                    <td><input v-model="row.metric" class="form-control form-control-sm" placeholder="e.g. % roles filled internally"></td>
                    <td><input v-model="row.target" class="form-control form-control-sm" placeholder="Expected value"></td>
                    <td><input v-model="row.actual" class="form-control form-control-sm" placeholder="Measured value"></td>
                    <td><input v-model="row.notes" class="form-control form-control-sm" placeholder="Comments"></td>
                    <td><button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger w-100" @click="removeMetric(idx)">✕</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary mt-2" @click="addMetric">+ Add row</button>
          </div>
        </div>

        <!-- 11 Sign-off -->
        <div class="rdp-section">
          <div class="rdp-section-label"><i class="bx bx-pen"></i> 11. Sign-Off <small class="fw-normal text-muted">(required for PDF)</small></div>
          <div class="rdp-section-body">
        <p class="small text-muted mb-3">
          HR Director, Finance Lead, Legal, and CEO/Sponsor must each provide name, date, and a signature (draw or upload image). Download stays disabled until all four are complete.
        </p>
        <div v-for="slot in signerSlots" :key="slot.key" class="sign-card mb-3">
          <div class="fw-semibold mb-2">{{ slot.label }}</div>
          <div class="row g-2 align-items-end">
            <div class="col-md-4">
              <label class="form-label small">Name</label>
              <input v-model="details.signatures[slot.key].name" type="text" class="form-control form-control-sm" placeholder="Full name">
            </div>
            <div class="col-md-3">
              <label class="form-label small">Date</label>
              <input v-model="details.signatures[slot.key].date" type="date" class="form-control form-control-sm">
            </div>
            <div class="col-md-5">
              <label class="form-label small">Signature — draw or upload</label>
              <div class="d-flex flex-wrap gap-2 align-items-center">
                <canvas
                  :data-sig="slot.key"
                  :ref="(el) => bindSigCanvas(slot.key, el)"
                  class="sig-canvas border rounded bg-white"
                  width="400"
                  height="120"
                />
                <div class="d-flex flex-column gap-1">
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" @click="clearSignature(slot.key)">Clear pad</button>
                  <label class="hr-btn hr-btn-sm hr-btn-outline-primary mb-0">
                    Upload
                    <input type="file" class="d-none" accept="image/*" @change="onSignatureFile(slot.key, $event)">
                  </label>
                </div>
              </div>
              <div v-if="details.signatures[slot.key].signature_data" class="small text-success mt-1">Signature captured.</div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
          <div class="rdp-download-status" :class="canDownloadCurrent ? 'rdp-download-ready' : 'rdp-download-pending'">
            <i :class="canDownloadCurrent ? 'bx bx-check-circle' : 'bx bx-info-circle'"></i>
            {{ downloadBlockedReason }}
          </div>
          <div class="d-flex gap-2">
            <button type="button" class="rdp-btn rdp-btn-ghost" @click="resetForm">
              <i class="bx bx-reset"></i> Reset
            </button>
            <button type="button" class="rdp-btn rdp-btn-primary" :disabled="saving" @click="savePlan">
              <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
              {{ saving ? 'Saving...' : (form.id ? 'Update Plan' : 'Create Plan') }}
            </button>
          </div>
        </div>

        <div v-if="message" class="rdp-alert mt-3" :class="alertClass">
          <div class="rdp-alert-icon">
            <i :class="alertIcon"></i>
          </div>
          <div class="rdp-alert-content">
            <div class="rdp-alert-message">{{ message }}</div>
          </div>
          <button class="rdp-alert-close" @click="message = ''" aria-label="Close alert">
            <i class="bx bx-x"></i>
          </button>
        </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

const workflowStatuses = ["Not Started", "In Progress", "Complete"];
const legalReviewStatuses = ["Pending", "Approved", "Rejected"];
const riskLevels = ["High", "Medium", "Low"];

const signerSlots = [
  { key: "hr_director", label: "HR Director" },
  { key: "finance_lead", label: "Finance Lead" },
  { key: "legal", label: "Legal" },
  { key: "ceo_sponsor", label: "CEO / Sponsor" }
];

const emptyBusinessCase = () => ({
  case_type: "",
  title: "",
  description: "",
  expected_benefit: "",
  risk_if_not: ""
});

const emptyImpactedRole = () => ({
  current_role: "",
  employee_name: "",
  department: "",
  proposed_new_role: "",
  new_dept_location: "",
  skills_gap: "",
  consultation_status: "Not Started"
});

const emptyTimeline = () => ({
  due_date: "",
  activity: "",
  owner: "",
  status: "Not Started"
});

const emptyTraining = () => ({
  employee_name: "",
  skills_gap: "",
  training_required: "",
  duration: "",
  cost: "",
  provider: "",
  completion_date: ""
});

const emptyRisk = () => ({
  risk: "",
  likelihood: "Medium",
  impact: "Medium",
  mitigation: "",
  owner: ""
});

const emptyMetric = () => ({
  metric: "",
  target: "",
  actual: "",
  notes: ""
});

const emptySigner = () => ({
  name: "",
  date: "",
  signature_data: ""
});

const defaultConsultation = () => ({
  key_messages: "",
  channels: "",
  feedback_mechanism: "",
  union_details: ""
});

const defaultTerms = () => ({
  salary_change: "",
  grade_change: "",
  location_change: "",
  working_hours_change: "",
  reporting_line_change: "",
  benefits_change: "",
  legal_hr_review_status: "Pending"
});

const defaultSignatures = () => ({
  hr_director: emptySigner(),
  finance_lead: emptySigner(),
  legal: emptySigner(),
  ceo_sponsor: emptySigner()
});

const makeDefaultDetails = () => ({
  business_cases: [emptyBusinessCase()],
  impacted_roles: [emptyImpactedRole()],
  selection_criteria: "",
  timeline: [emptyTimeline()],
  consultation: defaultConsultation(),
  training_support: [emptyTraining()],
  terms_conditions: defaultTerms(),
  risk_mitigation: [emptyRisk()],
  success_metrics: [emptyMetric()],
  signatures: defaultSignatures()
});

function mergeDetails(raw) {
  const d = makeDefaultDetails();
  if (!raw || typeof raw !== "object") return d;
  if (Array.isArray(raw.business_cases) && raw.business_cases.length) d.business_cases = raw.business_cases.map((x) => ({ ...emptyBusinessCase(), ...x }));
  if (Array.isArray(raw.impacted_roles) && raw.impacted_roles.length) d.impacted_roles = raw.impacted_roles.map((x) => ({ ...emptyImpactedRole(), ...x }));
  if (typeof raw.selection_criteria === "string") d.selection_criteria = raw.selection_criteria;
  if (Array.isArray(raw.timeline) && raw.timeline.length) d.timeline = raw.timeline.map((x) => ({ ...emptyTimeline(), ...x }));
  if (raw.consultation && typeof raw.consultation === "object") d.consultation = { ...defaultConsultation(), ...raw.consultation };
  if (Array.isArray(raw.training_support) && raw.training_support.length) {
    d.training_support = raw.training_support.map((x) => ({ ...emptyTraining(), ...x }));
  }
  if (raw.terms_conditions && typeof raw.terms_conditions === "object") d.terms_conditions = { ...defaultTerms(), ...raw.terms_conditions };
  if (Array.isArray(raw.risk_mitigation) && raw.risk_mitigation.length) {
    d.risk_mitigation = raw.risk_mitigation.map((x) => ({ ...emptyRisk(), ...x }));
  }
  if (Array.isArray(raw.success_metrics) && raw.success_metrics.length) {
    d.success_metrics = raw.success_metrics.map((x) => ({ ...emptyMetric(), ...x }));
  }
  if (raw.signatures && typeof raw.signatures === "object") {
    const sig = defaultSignatures();
    signerSlots.forEach((s) => {
      if (raw.signatures[s.key]) sig[s.key] = { ...emptySigner(), ...raw.signatures[s.key] };
    });
    d.signatures = sig;
  }
  return d;
}

function parseDetailsFromRow(row) {
  const raw = row?.plan_details_json;
  if (!raw) return makeDefaultDetails();
  try {
    return mergeDetails(JSON.parse(raw));
  } catch (_) {
    return makeDefaultDetails();
  }
}

function signaturesComplete(details) {
  if (!details?.signatures) return false;
  return signerSlots.every((s) => {
    const x = details.signatures[s.key];
    return (
      x &&
      String(x.name || "").trim() !== "" &&
      String(x.date || "").trim() !== "" &&
      String(x.signature_data || "").length > 24
    );
  });
}

const makeDefaultForm = () => ({
  id: null,
  plan_id: "",
  plan_title: "",
  objective: "",
  scope: "",
  sponsor: "",
  effective_date: "",
  review_date: "",
  status: "Draft"
});

export default {
  name: "ModuleRedeploymentPlan",
  components: { HrPageLayout },
  data() {
    return {
      statuses: ["Draft", "Submitted", "Approved", "Rejected"],
      workflowStatuses,
      legalReviewStatuses,
      riskLevels,
      signerSlots,
      allowed: false,
      canManage: false,
      saving: false,
      downloadingPdf: false,
      loadingList: false,
      plans: [],
      total: 0,
      page: 1,
      limit: 30,
      editorOpen: false,
      form: makeDefaultForm(),
      details: makeDefaultDetails(),
      message: "",
      ok: false,
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { label: "Home", route: "/dashboard" },
        { label: "HR Actions" },
        { label: "Redeployment Plan" }
      ];
    },
    totalPages() {
      return this.limit > 0 ? Math.ceil(this.total / this.limit) : 0;
    },
    canDownloadCurrent() {
      return this.signaturesComplete(this.details);
    },
    downloadBlockedReason() {
      if (this.canDownloadCurrent) return "All required signatories complete — you may download the PDF.";
      return "PDF download unlocks when HR Director, Finance Lead, Legal, and CEO/Sponsor have each signed digitally.";
    },
    alertClass() {
      return this.ok ? 'rdp-alert-success' : 'rdp-alert-danger';
    },
    alertIcon() {
      return this.ok ? 'bx bx-check-circle' : 'bx bx-error-circle';
    }
  },
  created() {
    this.allowed = getRight(["REDEPLOYMENT_VIEW", "REDEPLOYMENT_PLAN_CREATE", "REDEPLOYMENT_MANAGE"]);
    this.canManage = getRight(["REDEPLOYMENT_PLAN_CREATE", "REDEPLOYMENT_MANAGE"]);
    if (this.allowed) this.loadPlans();
  },
  methods: {
    signaturesComplete,
    statusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'approved') return 'rdp-status-approved';
      if (s === 'submitted') return 'rdp-status-submitted';
      if (s === 'rejected') return 'rdp-status-rejected';
      if (s === 'draft') return 'rdp-status-draft';
      return 'rdp-status-draft';
    },
    canDownloadRow(row) {
      return signaturesComplete(parseDetailsFromRow(row));
    },
    rowDownloadReason(row) {
      return this.canDownloadRow(row) ? "" : "All four signatories must sign before download.";
    },
    setMsg(ok, msg) {
      this.ok = !!ok;
      this.message = msg || "";
    },
    resetForm() {
      this.form = makeDefaultForm();
      this.details = makeDefaultDetails();
      this.setMsg(false, "");
    },
    openCreatePlan() {
      this.resetForm();
      this.editorOpen = true;
      this.$nextTick(() => {
        const el = typeof document !== "undefined" ? document.getElementById("plan-editor") : null;
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    closeEditor() {
      this.editorOpen = false;
    },
    csvEscape(val) {
      const s = String(val == null ? "" : val);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    },
    exportPlansCsv() {
      const headers = ["plan_id", "plan_title", "sponsor", "effective_date", "review_date", "status"];
      const lines = [headers.join(",")];
      this.plans.forEach((r) => {
        lines.push(headers.map((h) => this.csvEscape(r[h])).join(","));
      });
      const bom = "\uFEFF";
      const blob = new Blob([bom + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `redeployment-plans-${new Date().toISOString().slice(0, 10)}.csv`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    exportPlanRowCsv(row) {
      const headers = ["plan_id", "plan_title", "objective", "scope", "sponsor", "effective_date", "review_date", "status"];
      const lines = [headers.join(",")];
      lines.push(headers.map((h) => this.csvEscape(row[h])).join(","));
      const bom = "\uFEFF";
      const blob = new Blob([bom + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = String(row.plan_title || row.plan_id || "plan")
        .replace(/\s+/g, "-")
        .slice(0, 48)
        .replace(/[^a-zA-Z0-9-_]/g, "");
      a.download = `redeployment-plan-${slug || "export"}.csv`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    bindSigCanvas(key, el) {
      if (!el || !this.details.signatures[key]) return;
      if (el._hrmisSigBound === key) return;
      el._hrmisSigBound = key;
      const ctx = el.getContext("2d");
      ctx.strokeStyle = "#111";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      let drawing = false;
      const getPos = (e) => {
        const r = el.getBoundingClientRect();
        const cx = "touches" in e ? e.touches[0].clientX : e.clientX;
        const cy = "touches" in e ? e.touches[0].clientY : e.clientY;
        return { x: cx - r.left, y: cy - r.top };
      };
      const start = (e) => {
        e.preventDefault();
        drawing = true;
        ctx.beginPath();
        const p = getPos(e);
        ctx.moveTo(p.x, p.y);
      };
      const move = (e) => {
        if (!drawing) return;
        e.preventDefault();
        const p = getPos(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      };
      const end = (e) => {
        if (!drawing) return;
        e.preventDefault();
        drawing = false;
        try {
          this.details.signatures[key].signature_data = el.toDataURL("image/jpeg", 0.82);
        } catch (_) {}
      };
      el.addEventListener("mousedown", start);
      el.addEventListener("mousemove", move);
      el.addEventListener("mouseup", end);
      el.addEventListener("mouseleave", end);
      el.addEventListener("touchstart", start, { passive: false });
      el.addEventListener("touchmove", move, { passive: false });
      el.addEventListener("touchend", end);
    },
    clearSignature(key) {
      const c = this.$el?.querySelector?.(`canvas[data-sig="${key}"]`);
      if (c) {
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, c.width, c.height);
        c._hrmisSigBound = null;
      }
      if (this.details.signatures[key]) this.details.signatures[key].signature_data = "";
    },
    onSignatureFile(key, evt) {
      const file = evt.target.files && evt.target.files[0];
      evt.target.value = "";
      if (!file || !file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const data = reader.result;
        if (typeof data === "string" && data.length > 24) {
          this.details.signatures[key].signature_data = data;
        }
      };
      reader.readAsDataURL(file);
    },
    statusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "approved") return "text-bg-success";
      if (s === "submitted") return "text-bg-primary";
      if (s === "rejected") return "text-bg-danger";
      return "text-bg-secondary";
    },
    editPlan(row) {
      this.form = {
        id: row.id || null,
        plan_id: row.plan_id || "",
        plan_title: row.plan_title || "",
        objective: row.objective || "",
        scope: row.scope || "",
        sponsor: row.sponsor || "",
        effective_date: row.effective_date || "",
        review_date: row.review_date || "",
        status: this.statuses.includes(row.status) ? row.status : "Draft"
      };
      this.details = parseDetailsFromRow(row);
      this.setMsg(false, "");
      this.editorOpen = true;
      this.$nextTick(() => {
        const el = typeof document !== "undefined" ? document.getElementById("plan-editor") : null;
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        this.signerSlots.forEach((s) => {
          const c = this.$el.querySelector(`canvas[data-sig="${s.key}"]`);
          if (!c) return;
          c._hrmisSigBound = null;
          this.bindSigCanvas(s.key, c);
        });
      });
    },
    validForm() {
      if (!this.form.plan_title.trim()) return "Plan title is required.";
      if (!this.form.objective.trim()) return "Objective is required.";
      if (!this.form.scope.trim()) return "Scope is required.";
      if (!this.form.sponsor.trim()) return "Sponsor is required.";
      if (!this.form.effective_date) return "Effective date is required.";
      return "";
    },
    async savePlan() {
      if (!this.canManage) {
        this.setMsg(false, "You do not have permission to save plans.");
        return;
      }
      const validation = this.validForm();
      if (validation) {
        this.setMsg(false, validation);
        return;
      }
      this.saving = true;
      this.setMsg(false, "");
      try {
        const fd = new FormData();
        fd.append("function", "saveRedeploymentPlan");
        if (this.form.id) fd.append("id", this.form.id);
        fd.append("plan_title", this.form.plan_title);
        fd.append("objective", this.form.objective);
        fd.append("scope", this.form.scope);
        fd.append("sponsor", this.form.sponsor);
        fd.append("effective_date", this.form.effective_date);
        fd.append("review_date", this.form.review_date || "");
        fd.append("status", this.form.status || "Draft");
        fd.append("plan_details_json", JSON.stringify(this.details));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          this.setMsg(true, res?.data?.message || "Plan saved.");
          const newId = res?.data?.data?.id;
          const newPlanId = res?.data?.data?.plan_id;
          if (newId) this.form.id = Number(newId);
          if (newPlanId) this.form.plan_id = String(newPlanId);
          await this.loadPlans();
        } else {
          this.setMsg(false, res?.data?.message || "Save failed.");
        }
      } catch (e) {
        this.setMsg(false, e?.response?.data?.message || "Save failed.");
      } finally {
        this.saving = false;
      }
    },
    async loadPlans() {
      this.loadingList = true;
      try {
        const fd = new FormData();
        fd.append("function", "getRedeploymentPlanList");
        fd.append("limit", this.limit);
        fd.append("page", this.page);
        const res = await execute(fd, authUrl);
        const payload = res?.data?.success ? (res.data.data || {}) : {};
        this.plans = Array.isArray(payload.data) ? payload.data : [];
        this.total = Number(payload.total || 0);
      } catch (_) {
        this.plans = [];
        this.total = 0;
      } finally {
        this.loadingList = false;
      }
    },
    addBusinessCase() {
      this.details.business_cases.push(emptyBusinessCase());
    },
    removeBusinessCase(idx) {
      if (this.details.business_cases.length <= 1) {
        this.details.business_cases.splice(0, 1, emptyBusinessCase());
      } else {
        this.details.business_cases.splice(idx, 1);
      }
    },
    addImpactedRole() {
      this.details.impacted_roles.push(emptyImpactedRole());
    },
    removeImpactedRole(idx) {
      if (this.details.impacted_roles.length <= 1) {
        this.details.impacted_roles.splice(0, 1, emptyImpactedRole());
      } else {
        this.details.impacted_roles.splice(idx, 1);
      }
    },
    addTimeline() {
      this.details.timeline.push(emptyTimeline());
    },
    removeTimeline(idx) {
      if (this.details.timeline.length <= 1) {
        this.details.timeline.splice(0, 1, emptyTimeline());
      } else {
        this.details.timeline.splice(idx, 1);
      }
    },
    addTraining() {
      this.details.training_support.push(emptyTraining());
    },
    removeTraining(idx) {
      if (this.details.training_support.length <= 1) {
        this.details.training_support.splice(0, 1, emptyTraining());
      } else {
        this.details.training_support.splice(idx, 1);
      }
    },
    addRisk() {
      this.details.risk_mitigation.push(emptyRisk());
    },
    removeRisk(idx) {
      if (this.details.risk_mitigation.length <= 1) {
        this.details.risk_mitigation.splice(0, 1, emptyRisk());
      } else {
        this.details.risk_mitigation.splice(idx, 1);
      }
    },
    addMetric() {
      this.details.success_metrics.push(emptyMetric());
    },
    removeMetric(idx) {
      if (this.details.success_metrics.length <= 1) {
        this.details.success_metrics.splice(0, 1, emptyMetric());
      } else {
        this.details.success_metrics.splice(idx, 1);
      }
    },
    txt(v) {
      return String(v == null ? "" : v);
    },
    async downloadPlanPdf(sourceRow) {
      const row = sourceRow || null;
      let formSnap = { ...this.form };
      let det = JSON.parse(JSON.stringify(this.details));
      if (row) {
        formSnap = {
          id: row.id,
          plan_id: row.plan_id,
          plan_title: row.plan_title,
          objective: row.objective,
          scope: row.scope,
          sponsor: row.sponsor,
          effective_date: row.effective_date,
          review_date: row.review_date,
          status: row.status
        };
        det = parseDetailsFromRow(row);
      }
      if (!signaturesComplete(det)) {
        this.setMsg(false, "All four signatories (HR Director, Finance Lead, Legal, CEO/Sponsor) must complete name, date, and signature before download.");
        return;
      }
      this.downloadingPdf = true;
      try {
        const jsPDF = (await import("jspdf")).default;
        const autoTable = (await import("jspdf-autotable")).default;
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const left = 40;
        const pageW = 515;
        let y = 45;

        const checkY = (h) => {
          if (y + h > 780) {
            doc.addPage();
            y = 45;
          }
        };

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text("Redeployment Plan", left, y);
        y += 22;
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Plan ID: ${this.txt(formSnap.plan_id != null ? formSnap.plan_id : row?.plan_id)}`, left, y);
        y += 14;
        doc.text(`Title: ${this.txt(formSnap.plan_title)}`, left, y);
        y += 14;
        doc.text(`Sponsor: ${this.txt(formSnap.sponsor)}`, left, y);
        y += 14;
        doc.text(`Effective: ${this.txt(formSnap.effective_date)}   Review: ${this.txt(formSnap.review_date)}`, left, y);
        y += 14;
        doc.text(`Status: ${this.txt(formSnap.status)}`, left, y);
        y += 20;

        const addPara = (title, text) => {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          checkY(30);
          doc.text(title, left, y);
          y += 14;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          const lines = doc.splitTextToSize(this.txt(text), pageW);
          lines.forEach((ln) => {
            checkY(14);
            doc.text(ln, left, y);
            y += 12;
          });
          y += 8;
        };

        addPara("Objective", formSnap.objective);
        addPara("Scope", formSnap.scope);

        checkY(40);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("2. Business Cases", left, y);
        y += 16;
        autoTable(doc, {
          startY: y,
          head: [["Case Type", "Title", "Description", "Expected Benefit", "Risk if Not Implemented"]],
          body: det.business_cases.map((b) => [
            this.txt(b.case_type),
            this.txt(b.title),
            this.txt(b.description),
            this.txt(b.expected_benefit),
            this.txt(b.risk_if_not)
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [240, 240, 240] },
          margin: { left: left }
        });
        y = doc.lastAutoTable.finalY + 16;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(20);
        doc.text("3. Impacted Roles & Employees", left, y);
        y += 14;
        autoTable(doc, {
          startY: y,
          head: [["Current Role", "Employee", "Department", "Proposed Role", "New Dept/Loc", "Skills Gap", "Consultation"]],
          body: det.impacted_roles.map((r) => [
            this.txt(r.current_role),
            this.txt(r.employee_name),
            this.txt(r.department),
            this.txt(r.proposed_new_role),
            this.txt(r.new_dept_location),
            this.txt(r.skills_gap),
            this.txt(r.consultation_status)
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [240, 240, 240] },
          margin: { left: left }
        });
        y = doc.lastAutoTable.finalY + 16;

        addPara("4. Selection Criteria", det.selection_criteria);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(20);
        doc.text("5. Timeline & Milestones", left, y);
        y += 14;
        autoTable(doc, {
          startY: y,
          head: [["Due Date", "Activity", "Owner", "Status"]],
          body: det.timeline.map((t) => [this.txt(t.due_date), this.txt(t.activity), this.txt(t.owner), this.txt(t.status)]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [240, 240, 240] },
          margin: { left: left }
        });
        y = doc.lastAutoTable.finalY + 16;

        addPara("6a. Key Messages", det.consultation.key_messages);
        addPara("6b. Channels", det.consultation.channels);
        doc.setFontSize(9);
        checkY(30);
        doc.setFont("helvetica", "bold");
        doc.text("6c. Feedback Mechanism", left, y);
        y += 12;
        doc.setFont("helvetica", "normal");
        doc.text(this.txt(det.consultation.feedback_mechanism), left, y);
        y += 18;
        doc.setFont("helvetica", "bold");
        doc.text("6d. Union/Consultation Details", left, y);
        y += 12;
        doc.setFont("helvetica", "normal");
        doc.text(this.txt(det.consultation.union_details), left, y);
        y += 22;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(20);
        doc.text("7. Training & Support", left, y);
        y += 14;
        autoTable(doc, {
          startY: y,
          head: [["Employee", "Skills Gap", "Training", "Duration", "Cost", "Provider", "Completion"]],
          body: det.training_support.map((t) => [
            this.txt(t.employee_name),
            this.txt(t.skills_gap),
            this.txt(t.training_required),
            this.txt(t.duration),
            this.txt(t.cost),
            this.txt(t.provider),
            this.txt(t.completion_date)
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [240, 240, 240] },
          margin: { left: left }
        });
        y = doc.lastAutoTable.finalY + 16;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(20);
        doc.text("8. Terms & Conditions Changes", left, y);
        y += 14;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const tc = det.terms_conditions;
        [
          ["Salary", tc.salary_change],
          ["Grade", tc.grade_change],
          ["Location", tc.location_change],
          ["Working Hours", tc.working_hours_change],
          ["Reporting Line", tc.reporting_line_change],
          ["Benefits", tc.benefits_change],
          ["Legal/HR Review", tc.legal_hr_review_status]
        ].forEach(([lab, val]) => {
          checkY(14);
          doc.text(`${lab}: ${this.txt(val)}`, left, y);
          y += 12;
        });
        y += 10;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(20);
        doc.text("9. Risk & Mitigation", left, y);
        y += 14;
        autoTable(doc, {
          startY: y,
          head: [["Risk", "Likelihood", "Impact", "Mitigation", "Owner"]],
          body: det.risk_mitigation.map((r) => [
            this.txt(r.risk),
            this.txt(r.likelihood),
            this.txt(r.impact),
            this.txt(r.mitigation),
            this.txt(r.owner)
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [240, 240, 240] },
          margin: { left: left }
        });
        y = doc.lastAutoTable.finalY + 16;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(20);
        doc.text("10. Success Metrics", left, y);
        y += 14;
        autoTable(doc, {
          startY: y,
          head: [["Metric", "Target", "Actual", "Notes"]],
          body: det.success_metrics.map((m) => [this.txt(m.metric), this.txt(m.target), this.txt(m.actual), this.txt(m.notes)]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [240, 240, 240] },
          margin: { left: left }
        });
        y = doc.lastAutoTable.finalY + 24;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        checkY(24);
        doc.text("11. Sign-Off", left, y);
        y += 18;

        for (const slot of signerSlots) {
          const s = det.signatures[slot.key];
          checkY(110);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(slot.label, left, y);
          y += 12;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.text(`Name: ${this.txt(s?.name)}`, left, y);
          y += 12;
          doc.text(`Date: ${this.txt(s?.date)}`, left, y);
          y += 10;
          const img = s?.signature_data;
          if (img && String(img).startsWith("data:image")) {
            try {
              const fmt = img.indexOf("image/png") !== -1 ? "PNG" : "JPEG";
              doc.addImage(img, fmt, left, y, 160, 48);
              y += 56;
            } catch (_) {
              doc.text("(signature image)", left, y);
              y += 14;
            }
          }
          y += 16;
        }

        const fname = `redeployment-plan-${this.txt(formSnap.plan_title).replace(/\s+/g, "-").slice(0, 40) || "export"}.pdf`;
        doc.save(fname);
      } catch (e) {
        console.error(e);
        this.setMsg(false, "Could not generate PDF.");
      } finally {
        this.downloadingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────── */
.plan-wrap { width: 100%; padding-bottom: 40px; }

/* ─── No Access ──────────────────────────────────────────── */
.rdp-no-access {
  text-align: center;
  padding: 80px 24px;
  color: #64748b;
}
.rdp-no-access-icon {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px; color: #94a3b8;
}

/* ─── Toolbar ────────────────────────────────────────────── */
.rdp-toolbar-modern {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px 12px 0 0;
  border-bottom: none;
}
.rdp-toolbar-tabs { display: flex; gap: 6px; }
.rdp-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.rdp-tab:hover { background: #f1f5f9; color: #334155; }
.rdp-tab-active {
  background: #0c4a6e;
  color: #fff;
  border-color: #0c4a6e;
  box-shadow: 0 2px 8px rgba(12,74,110,0.25);
}
.rdp-tab-badge {
  background: rgba(255,255,255,0.25);
  border-radius: 20px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
}
.rdp-tab:not(.rdp-tab-active) .rdp-tab-badge {
  background: #e2e8f0;
  color: #475569;
}
.rdp-toolbar-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }

/* ─── Buttons ────────────────────────────────────────────── */
.rdp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s;
  white-space: nowrap;
}
.rdp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.rdp-btn-primary { background: #0c4a6e; color: #fff; border-color: #0c4a6e; }
.rdp-btn-primary:hover:not(:disabled) { background: #0e5a85; box-shadow: 0 2px 8px rgba(12,74,110,0.3); }
.rdp-btn-ghost { background: transparent; color: #475569; border-color: #e2e8f0; }
.rdp-btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.rdp-btn-outline { background: #fff; color: #0c4a6e; border-color: #0c4a6e; }
.rdp-btn-outline:hover:not(:disabled) { background: #eff6ff; }
.rdp-btn-outline-success { background: #fff; color: #166534; border-color: #16a34a; }
.rdp-btn-outline-success:hover:not(:disabled) { background: #f0fdf4; }

/* ─── Card ───────────────────────────────────────────────── */
.rdp-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(15,23,42,0.06);
}
.rdp-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.rdp-card-title-group { display: flex; align-items: center; gap: 10px; }
.rdp-card-title {
  font-weight: 700;
  font-size: 13px;
  color: #0c4a6e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;
}
.rdp-badge-count {
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
}

/* ─── Empty / Loading ────────────────────────────────────── */
.rdp-empty { text-align: center; padding: 60px 24px; color: #64748b; }
.rdp-empty-icon {
  width: 64px; height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
  font-size: 28px; color: #94a3b8;
}
.rdp-empty h5 { color: #334155; margin-bottom: 6px; }
.rdp-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 40px 24px;
  color: #64748b;
  font-size: 14px;
}
.rdp-spinner {
  width: 22px; height: 22px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #0c4a6e;
  border-radius: 50%;
  animation: rdp-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes rdp-spin { to { transform: rotate(360deg); } }

/* ─── Table ──────────────────────────────────────────────── */
.rdp-table-wrap { overflow-x: auto; }
.rdp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.rdp-table thead th {
  background: #f1f5f9;
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}
.rdp-table tbody td {
  padding: 11px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  color: #334155;
}
.rdp-tr:hover td { background: #f8fafc; }
.rdp-td-num { color: #94a3b8; font-size: 12px; text-align: center; font-weight: 600; }
.rdp-plan-id {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #0c4a6e;
  font-weight: 700;
  background: #eff6ff;
  padding: 2px 8px;
  border-radius: 4px;
}
.rdp-plan-title { font-weight: 600; color: #1e293b; }

/* ─── Status Pills ───────────────────────────────────────── */
.rdp-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.rdp-status-approved { background: #dcfce7; color: #15803d; }
.rdp-status-submitted { background: #dbeafe; color: #1d4ed8; }
.rdp-status-rejected { background: #fee2e2; color: #b91c1c; }
.rdp-status-draft { background: #f1f5f9; color: #475569; }

/* ─── Row Actions ────────────────────────────────────────── */
.rdp-row-actions { display: flex; gap: 4px; }
.rdp-action-btn {
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  font-size: 15px;
  color: #64748b;
  transition: all 0.15s;
}
.rdp-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.rdp-action-edit:hover { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.rdp-action-download:hover:not(:disabled) { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
.rdp-action-export:hover { background: #fefce8; color: #92400e; border-color: #fde68a; }

/* ─── Pagination ─────────────────────────────────────────── */
.rdp-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #64748b;
  background: #fafafa;
}
.rdp-pages { display: flex; gap: 4px; }
.rdp-page-btn {
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 16px;
  color: #475569;
  transition: all 0.15s;
}
.rdp-page-btn:hover:not(:disabled) { background: #f1f5f9; }
.rdp-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ─── Editor Card ────────────────────────────────────────── */
.rdp-editor-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(15,23,42,0.06);
}
.rdp-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  background: linear-gradient(135deg, #0c4a6e 0%, #0e5a85 100%);
  color: #fff;
}
.rdp-editor-header-left { display: flex; align-items: center; gap: 14px; }
.rdp-editor-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.rdp-editor-title { font-size: 17px; font-weight: 700; margin: 0 0 3px; color: #fff; }
.rdp-editor-subtitle { font-size: 12px; color: rgba(255,255,255,0.75); margin: 0; }
.rdp-editor-header-actions { display: flex; gap: 8px; flex-shrink: 0; }
.rdp-editor-header .rdp-btn-ghost {
  color: rgba(255,255,255,0.85);
  border-color: rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.1);
}
.rdp-editor-header .rdp-btn-ghost:hover { background: rgba(255,255,255,0.2); }
.rdp-editor-header .rdp-btn-outline-success {
  background: rgba(255,255,255,0.1);
  color: #fff;
  border-color: rgba(255,255,255,0.4);
}
.rdp-editor-header .rdp-btn-outline-success:hover:not(:disabled) {
  background: rgba(255,255,255,0.2);
}

/* Progress bar */
.rdp-progress-bar {
  height: 3px;
  background: #e2e8f0;
}
.rdp-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #16a34a);
  transition: width 0.4s ease;
}

.rdp-editor-body { padding: 22px; }

/* ─── Sections ───────────────────────────────────────────── */
.rdp-section {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: box-shadow 0.15s;
}
.rdp-section:focus-within { box-shadow: 0 0 0 3px rgba(12,74,110,0.08); }
.rdp-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 700;
  font-size: 12px;
  color: #0c4a6e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.rdp-section-label i { font-size: 16px; color: #0c4a6e; }
.rdp-section-hint { font-weight: 400; text-transform: none; color: #94a3b8; font-size: 11px; margin-left: 4px; }
.rdp-section-body { padding: 16px; }

/* ─── Download status ────────────────────────────────────── */
.rdp-download-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 6px;
}
.rdp-download-ready { background: #f0fdf4; color: #15803d; }
.rdp-download-pending { background: #f8fafc; color: #64748b; }

/* ─── Alert ──────────────────────────────────────────────── */
.rdp-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: rdp-alert-slide-in 0.3s ease-out;
  position: relative;
  overflow: hidden;
}

@keyframes rdp-alert-slide-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.rdp-alert::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}

.rdp-alert-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rdp-alert-icon i {
  font-size: 20px;
  line-height: 1;
}

.rdp-alert-content {
  flex: 1;
  min-width: 0;
}

.rdp-alert-message {
  line-height: 1.5;
  word-wrap: break-word;
}

.rdp-alert-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  padding: 0;
  margin-left: 4px;
}

.rdp-alert-close i {
  font-size: 18px;
  line-height: 1;
}

.rdp-alert-close:hover {
  background: rgba(0, 0, 0, 0.08);
}

/* Success Alert */
.rdp-alert-success {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #15803d;
  border: 1px solid #86efac;
}

.rdp-alert-success::before {
  background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
}

.rdp-alert-success .rdp-alert-icon {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.rdp-alert-success .rdp-alert-close {
  color: #15803d;
}

.rdp-alert-success .rdp-alert-close:hover {
  background: rgba(22, 163, 74, 0.15);
}

/* Danger/Error Alert */
.rdp-alert-danger {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.rdp-alert-danger::before {
  background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
}

.rdp-alert-danger .rdp-alert-icon {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.rdp-alert-danger .rdp-alert-close {
  color: #b91c1c;
}

.rdp-alert-danger .rdp-alert-close:hover {
  background: rgba(220, 38, 38, 0.15);
}

/* Warning Alert (for future use) */
.rdp-alert-warning {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  color: #92400e;
  border: 1px solid #fcd34d;
}

.rdp-alert-warning::before {
  background: linear-gradient(180deg, #f59e0b 0%, #d97706 100%);
}

.rdp-alert-warning .rdp-alert-icon {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

.rdp-alert-warning .rdp-alert-close {
  color: #92400e;
}

.rdp-alert-warning .rdp-alert-close:hover {
  background: rgba(217, 119, 6, 0.15);
}

/* Info Alert (for future use) */
.rdp-alert-info {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.rdp-alert-info::before {
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
}

.rdp-alert-info .rdp-alert-icon {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}

.rdp-alert-info .rdp-alert-close {
  color: #1e40af;
}

.rdp-alert-info .rdp-alert-close:hover {
  background: rgba(37, 99, 235, 0.15);
}


/* ─── Repeat blocks ──────────────────────────────────────── */
.repeat-block {
  padding: 14px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  transition: border-color 0.15s;
}
.repeat-block:focus-within { border-color: #93c5fd; background: #fff; }

/* ─── Sign cards ─────────────────────────────────────────── */
.sign-card {
  padding: 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.15s;
}
.sign-card:focus-within { border-color: #93c5fd; }
.sig-canvas {
  cursor: crosshair;
  touch-action: none;
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

/* ─── Legacy hr-btn (used in section bodies) ─────────────── */
.hr-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 13px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s;
}
.hr-btn-sm { padding: 4px 10px; font-size: 11px; }
.hr-btn-primary { background: #0c4a6e; color: #fff; border-color: #0c4a6e; }
.hr-btn-primary:hover { background: #0e5a85; }
.hr-btn-secondary { background: #475569; color: #fff; border-color: #334155; }
.hr-btn-outline-primary { background: #fff; border-color: #0c4a6e; color: #0c4a6e; }
.hr-btn-outline-primary:hover { background: #eff6ff; }
.hr-btn-outline-secondary { background: #fff; border-color: #94a3b8; color: #475569; }
.hr-btn-outline-secondary:hover { background: #f8fafc; }
.hr-btn-outline-danger { background: transparent; border-color: #fca5a5; color: #b91c1c; }
.hr-btn-outline-danger:hover { background: #fef2f2; border-color: #f87171; }

/* ─── Misc ───────────────────────────────────────────────── */
.text-danger { color: #dc2626; }
.text-muted { color: #64748b; }
.mt-3 { margin-top: 12px; }
.me-2 { margin-right: 8px; }

.table-card {
  background: #fdfdfd;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 20px 24px;
  border: 1px solid #c5d4e0;
}
.rdp-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-top: 3px solid #0c4a6e;
}
.rdp-toolbar__text small {
  display: block;
  line-height: 1.55;
  color: #475569 !important;
}
.rdp-toolbar__title {
  color: #0f172a;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-size: 0.95rem;
}
.rdp-toolbar__actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.rdp-list-card__heading {
  font-weight: 700;
  color: #0c4a6e;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.8rem;
}
.rdp-table-wrap {
  border-radius: 2px;
  border: 1px solid #94a3b8;
  overflow: hidden;
}
.rdp-table { margin-bottom: 0; }
.rdp-table thead th {
  background: #e2e8f0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #334155;
  border-color: #94a3b8 !important;
  border-bottom: 2px solid #64748b !important;
}
.rdp-table tbody td {
  border-color: #cbd5e1 !important;
  vertical-align: middle;
}
.rdp-cell-title {
  font-weight: 600;
  color: #1e293b;
}
.rdp-th-actions { min-width: 220px; }
.rdp-actions { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.rdp-loading { color: #475569; }
.rdp-editor-card {
  border-left: 5px solid #0c4a6e;
  border-top: 1px solid #cbd5e1;
}
.rdp-editor-head {
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 14px;
}
.pagination-row { margin-top: 14px; display: flex; align-items: center; gap: 10px; color: #334155; }
.hr-btn {
  padding: 7px 14px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
}
.hr-btn-sm { padding: 5px 11px; font-size: 12px; }
.hr-btn-primary {
  background: #0c4a6e;
  color: #fff;
  border-color: #0a3d5c;
}
.hr-btn-primary:hover {
  background: #0e5a85;
}
.hr-btn-secondary { background: #475569; color: #fff; border-color: #334155; }
.hr-btn-success {
  background: #166534;
  color: #fff;
  border-color: #14532d;
}
.hr-btn-outline-primary {
  background: #fff;
  border: 1px solid #0c4a6e;
  color: #0c4a6e;
}
.hr-btn-outline-secondary {
  background: #fff;
  border: 1px solid #64748b;
  color: #475569;
}
.hr-btn-outline-danger { background: transparent; border: 1px solid #b91c1c; color: #b91c1c; }
.text-danger { color: #dc3545; }
.section-heading {
  margin-top: 1.35rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid #cbd5e1;
  font-weight: 700;
  font-size: 0.95rem;
  color: #0c4a6e;
  letter-spacing: 0.02em;
}
.repeat-block {
  padding: 14px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 2px;
  background: #f8fafc;
}
.sign-card {
  padding: 14px 16px;
  border: 1px solid #94a3b8;
  border-radius: 2px;
  background: #fff;
}
.sig-canvas { cursor: crosshair; touch-action: none; max-width: 100%; height: auto; }

/* Modern toolbar */
.rdp-toolbar-modern {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
}
.rdp-toolbar-tabs { display: flex; gap: 4px; }
.rdp-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.rdp-tab-active {
  background: #0c4a6e;
  color: #fff;
  border-color: #0c4a6e;
}
.rdp-tab-count {
  background: rgba(255,255,255,0.25);
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 11px;
}
.rdp-toolbar-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.rdp-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.rdp-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.rdp-btn-primary { background: #0c4a6e; color: #fff; border-color: #0a3d5c; }
.rdp-btn-primary:hover:not(:disabled) { background: #0e5a85; }
.rdp-btn-ghost { background: transparent; color: #475569; border-color: #cbd5e1; }
.rdp-btn-ghost:hover:not(:disabled) { background: #f1f5f9; }
.rdp-btn-outline { background: #fff; color: #0c4a6e; border-color: #0c4a6e; }
.rdp-btn-outline:hover:not(:disabled) { background: #f0f7ff; }
.rdp-btn-outline-success { background: #fff; color: #166534; border-color: #166534; }
.rdp-btn-outline-success:hover:not(:disabled) { background: #f0fdf4; }

/* Card */
.rdp-card {
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  background: #fff;
  overflow: hidden;
}
.rdp-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.rdp-card-title { font-weight: 700; color: #0c4a6e; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; }
.rdp-row-count { font-size: 12px; color: #64748b; }
.rdp-empty { text-align: center; padding: 48px 24px; color: #64748b; }
.rdp-empty i { font-size: 40px; color: #94a3b8; display: block; margin-bottom: 12px; }
.rdp-loading { display: flex; align-items: center; gap: 10px; padding: 32px 24px; color: #475569; }
.rdp-spinner {
  width: 20px; height: 20px;
  border: 2px solid #e2e8f0;
  border-top-color: #0c4a6e;
  border-radius: 50%;
  animation: rdp-spin 0.7s linear infinite;
}
@keyframes rdp-spin { to { transform: rotate(360deg); } }

/* Status pills */
.rdp-status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rdp-status-approved { background: #dcfce7; color: #166534; }
.rdp-status-submitted { background: #dbeafe; color: #1e40af; }
.rdp-status-rejected { background: #fee2e2; color: #991b1b; }
.rdp-status-draft { background: #f1f5f9; color: #475569; }

/* Row actions */
.rdp-row-actions { display: flex; gap: 4px; }
.rdp-action-btn {
  padding: 4px 8px;
  border-radius: 3px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #475569;
}
.rdp-action-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.rdp-action-edit:hover { background: #eff6ff; color: #1d4ed8; border-color: #93c5fd; }
.rdp-action-download:hover:not(:disabled) { background: #f0fdf4; color: #166534; border-color: #86efac; }
.rdp-action-export:hover { background: #fefce8; color: #854d0e; border-color: #fde047; }

/* Pagination */
.rdp-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
}
.rdp-pages { display: flex; gap: 4px; }
.rdp-page-btn {
  padding: 4px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
}
.rdp-page-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* Editor card */
.rdp-editor-card {
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  background: #fff;
}
.rdp-editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
}
.rdp-editor-title { font-size: 16px; font-weight: 700; color: #0c4a6e; margin: 0 0 4px; }
.rdp-editor-subtitle { font-size: 12px; color: #64748b; margin: 0; }
.rdp-editor-header-actions { display: flex; gap: 8px; flex-shrink: 0; }
.rdp-editor-body { padding: 20px; }

/* Sections */
.rdp-section {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  margin-bottom: 16px;
  overflow: hidden;
}
.rdp-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 700;
  font-size: 13px;
  color: #0c4a6e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rdp-section-hint { font-weight: 400; text-transform: none; color: #64748b; font-size: 11px; }
.rdp-section-body { padding: 14px; }

/* No access */
.rdp-no-access { text-align: center; padding: 60px 24px; color: #64748b; }
.rdp-no-access i { font-size: 48px; color: #94a3b8; display: block; margin-bottom: 16px; }

/* Table */
.rdp-td-num { color: #94a3b8; font-size: 12px; text-align: center; }
.rdp-plan-id { font-family: monospace; font-size: 12px; color: #0c4a6e; font-weight: 600; }
.rdp-plan-title { font-weight: 600; color: #1e293b; }
</style>
